import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import React, { useState } from 'react';
import {
  Popover,
  Card,
  Loader,
  Button,
  Stack,
  Box,
  ButtonGroup,
  Shelf,
  TextLink,
  Icon,
} from '@chaser/components';
import { bool, oneOfType, func, string, number, instanceOf } from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import cx from 'classnames';
import getDay from 'date-fns/getDay';
import setHours from 'date-fns/setHours';
import './ChaserDatePicker.css';
import style from './ChaserDatePicker.module.scss';

const formatInitialTime = (initialTimeHour, selectedDate) => {
  return setHours(selectedDate, initialTimeHour);
};

const getDateDay = date => getDay(date);

const DateTimePicker = ({
  defaultValue,
  className,
  inputClass,
  placeholderText,
  showTimeInput = false,
  onDateUpdated,
  onDateRemoved = () => {},
  removeDate = () => {},
  setDate = () => {},
  minDate = new Date(),
  minDateTime,
  loading = false,
  onCancel = () => {},
  closeOnSave = false,
  disabled = false,
  showDateInput = true,
  showDeleteButton = true,
  showActionButtons = true,
  timingInterval = 30,
  initialTimeHour,
  height = 'medium',
}) => {
  const [initialDate, setInitialDate] = useState(defaultValue);
  const [startDate, setStartDate] = useState(initialDate);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  function scrollToTime() {
    const id = setTimeout(() => {
      const selectedTimeNode = document.querySelector(
        '.react-datepicker__time-list-item.react-datepicker__time-list-item--selected',
      );
      const timeListNode = document.querySelectorAll('.react-datepicker__time-list-item')[20];
      if (selectedTimeNode) {
        selectedTimeNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (timeListNode) {
        timeListNode.scrollIntoView();
      }
    }, 500);
    return () => {
      clearTimeout(id);
    };
  }

  function toggleDropdownOpen(open) {
    if (showTimeInput) scrollToTime();
    setStartDate(initialDate);
    setDropdownOpen(open);
  }

  async function handleChange(date, e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    const time = initialTimeHour || date.getHours();
    const timeAlteredDate = startDate && showTimeInput ? date : formatInitialTime(time, date);

    if (!startDate || (getDateDay(date) !== getDateDay(startDate) && showTimeInput)) scrollToTime();

    if (date > new Date()) {
      if (!showActionButtons) setInitialDate(timeAlteredDate);
      setStartDate(timeAlteredDate);
    } else {
      setStartDate(timeAlteredDate);
    }

    if (!showActionButtons) applyDate(timeAlteredDate);
  }

  async function applyDate(date) {
    const response = await setDate(date);
    setInitialDate(date);
    if (onDateUpdated) await onDateUpdated(response);
    if (closeOnSave && showDateInput) setDropdownOpen(false);
  }

  async function deleteDate() {
    const response = await removeDate();
    setInitialDate('');
    if (onDateRemoved) await onDateRemoved(response);
    if (closeOnSave && showDateInput) setDropdownOpen(false);
  }

  const filterPassedTime = time => {
    const currentDate = new Date();
    const selectedDate = new Date(time);

    return currentDate.getTime() < selectedDate.getTime();
  };

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const actionButtons = (
    <ButtonGroup justifyContent="center" className={style.actions}>
      <TextLink
        as="button"
        type="button"
        trim={false}
        color="Dark Neutral 1"
        onClick={() => {
          setStartDate(initialDate);
          onCancel();
          setDropdownOpen(false);
        }}
      >
        Cancel
      </TextLink>
      <Button
        size="narrow"
        onClick={async () => {
          await applyDate(startDate);
        }}
      >
        Save
      </Button>
    </ButtonGroup>
  );

  const calendar = (
    <Box>
      {loading && (
        <div className={style.loadOverlay}>
          <Loader size={20} />
        </div>
      )}
      <Stack gap="xsmall" dividers>
        <DatePicker
          inline
          id="calendar"
          selected={startDate && new Date(startDate)}
          onChange={handleChange}
          className={style.calendar}
          timeIntervals={timingInterval}
          calendarClassName="calendar"
          shouldCloseOnSelect={false}
          minDate={minDateTime || minDate}
          filterTime={minDateTime && filterPassedTime}
          showTimeSelect={showTimeInput}
          showDisabledMonthNavigation
          disabled={disabled}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <Shelf justifyContent="space-between" alignItems="center">
              <Button
                icon={<Icon name="chevron-left" size={16} />}
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled || disabled}
                appearance="minimal"
                intent="muted"
              />
              <div className={style.date}>
                <span className={style.monthSelect}>{months[getMonth(date)]}</span>
                <span className={style.yearSelect}>{getYear(date)}</span>
              </div>
              <Button
                icon={<Icon name="chevron-right" size={16} />}
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled || disabled}
                appearance="minimal"
                intent="muted"
              />
            </Shelf>
          )}
        />
        {(showDeleteButton || showActionButtons) && (
          <Stack dividers gap="xsmall">
            {showActionButtons && actionButtons}
            {defaultValue !== '-' && showDeleteButton && (
              <ButtonGroup justifyContent="center" className={style.removeButton}>
                <TextLink
                  as="button"
                  type="button"
                  disabled={disabled}
                  color="Red"
                  onClick={() => {
                    setStartDate('');
                    deleteDate();
                  }}
                >
                  Remove date
                </TextLink>
              </ButtonGroup>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );

  if (!showDateInput) return <>{calendar}</>;
  return (
    <div className={className}>
      <Popover
        shown={dropdownOpen}
        onToggle={toggleDropdownOpen}
        target={
          <DatePicker
            className={cx(
              style.dateInput,
              style[`height-${height}`],
              disabled && style.disabled,
              inputClass,
            )}
            placeholderText={placeholderText || 'Enter date'}
            popperClassName="datepicker-hide"
            selected={startDate && new Date(startDate)}
            minDate={minDateTime || minDate}
            filterTime={minDateTime && filterPassedTime}
            onChange={handleChange}
            shouldCloseOnSelect={false}
            timeFormat="hh:mm"
            dateFormat={showTimeInput ? 'd MMM yyyy hh:mm aa' : 'd MMM yyyy'}
            disabled={disabled}
          />
        }
      >
        {!disabled && <Card>{calendar}</Card>}
      </Popover>
    </div>
  );
};

export default DateTimePicker;

DateTimePicker.propTypes = {
  initialTimeHour: oneOfType([string, number]),
  timingInterval: oneOfType([string, number]),
  showActionButtons: bool,
  showDeleteButton: bool,
  showDateInput: bool,
  closeOnSave: bool,
  minDate: oneOfType([string, instanceOf(Date)]),
  minDateTime: oneOfType([string, instanceOf(Date)]),
  loading: bool,
  showTimeInput: bool,
  disabled: bool,
  className: oneOfType([string, number]),
  removeDate: func,
  onCancel: func,
  setDate: func,
  onDateUpdated: func,
  onDateRemoved: func,
  inputClass: oneOfType([string, number]),
  placeholderText: string,
  defaultValue: oneOfType([string, instanceOf(Date)]),
  height: string,
};
